// PrimeVue deliberately closes a connected overlay when one of its scrollable
// parents moves. That is the right default for ordinary document overlays, but
// a field inside a dialog needs to remain available while the dialog content
// scrolls. BayHive uses PrimeVue's document portal for those fields so the
// panel can paint above the dialog header and is not clipped by
// `.p-dialog-content`; this small coordinator preserves that behavior and
// realigns the portal with its field on the next frame.
export function createDialogBodyOverlayScrollCoordinator() {
  let alignmentFrame = null;
  let alignOverlay = null;
  let dialogContent = null;
  let isVisible = null;
  let overlay = null;
  let tracking = false;
  let resizeObserver = null;

  const cancelAlignment = () => {
    if (alignmentFrame === null || typeof window === 'undefined') {
      alignmentFrame = null;
      return;
    }

    window.cancelAnimationFrame(alignmentFrame);
    alignmentFrame = null;
  };

  const unbind = () => {
    if (tracking && typeof document !== 'undefined') {
      document.removeEventListener('scroll', handleDialogContentScroll, true);
    }

    cancelAlignment();
    resizeObserver?.disconnect();
    resizeObserver = null;
    alignOverlay = null;
    dialogContent = null;
    isVisible = null;
    overlay = null;
    tracking = false;
  };

  const scheduleAlignment = () => {
    if (alignmentFrame !== null || typeof window === 'undefined') {
      return;
    }

    alignmentFrame = window.requestAnimationFrame(() => {
      alignmentFrame = null;

      if (!isVisible?.() || !alignOverlay) {
        unbind();
        return;
      }

      alignOverlay();
    });
  };

  const handleDialogContentScroll = (event) => {
    if (!isVisible?.() || !dialogContent || !overlay) {
      unbind();
      return;
    }

    const target = event.target;

    if (
      !target ||
      !dialogContent.contains(target) ||
      target === overlay ||
      overlay.contains(target)
    ) {
      return;
    }

    // Prevent PrimeVue's ConnectedOverlayScrollHandler from closing the field
    // overlay, then realign its body portal after the dialog has scrolled.
    event.stopPropagation();
    scheduleAlignment();
  };

  const bind = ({
    alignOverlay: nextAlignOverlay,
    appendTo,
    control,
    isVisible: nextIsVisible,
    overlay: nextOverlay,
  } = {}) => {
    unbind();

    if (
      appendTo !== 'body' ||
      !control?.closest ||
      !nextAlignOverlay ||
      !nextIsVisible?.() ||
      !nextOverlay ||
      typeof document === 'undefined'
    ) {
      return;
    }

    const nextDialogContent = control.closest('.p-dialog-content');

    alignOverlay = nextAlignOverlay;
    dialogContent = nextDialogContent;
    isVisible = nextIsVisible;
    overlay = nextOverlay;

    // A containing popover can resize and reposition after a picker opens.
    // Re-run PrimeVue's collision detection using the settled field position.
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleAlignment);
      new Set([control, overlay, control.closest('.p-popover'), control.closest('.p-dialog')])
        .forEach((element) => {
          if (element) resizeObserver.observe(element);
        });
    }
    scheduleAlignment();

    if (dialogContent) {
      document.addEventListener('scroll', handleDialogContentScroll, true);
      tracking = true;
    }
  };

  return {
    bind,
    unbind,
  };
}
